/**
 * ConfirmDialog — direction-agnostic confirmation modal.
 *
 * Imperative API via `useConfirm()` so callers stay declarative:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: t("workers.deleteTitle"),
 *     message: t("workers.deleteBody"),
 *     confirmLabel: t("common.delete"),
 *     destructive: true,
 *   });
 *   if (ok) mutation.mutate(...);
 *
 * Replaces scattered `window.confirm` calls on destructive actions.
 * Keyboard: Esc cancels, Enter confirms. Focus lands on Cancel by
 * default so an accidental Enter press does NOT delete anything.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

type ConfirmOpts = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type Resolver = (ok: boolean) => void;

let _open: ((opts: ConfirmOpts) => Promise<boolean>) | null = null;

export function useConfirm(): (opts: ConfirmOpts) => Promise<boolean> {
  return useCallback((opts: ConfirmOpts) => {
    if (!_open) {
      // ConfirmDialogProvider not mounted — fall back to the native
      // browser confirm so callers still work during early app boot.
      const ok = window.confirm(`${opts.title}\n\n${opts.message}`);
      return Promise.resolve(ok);
    }
    return _open(opts);
  }, []);
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [opts, setOpts] = useState<ConfirmOpts | null>(null);
  const resolverRef = useRef<Resolver | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    _open = (o) =>
      new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setOpts(o);
      });
    return () => {
      _open = null;
    };
  }, []);

  useEffect(() => {
    if (!opts) return;
    const t = setTimeout(() => cancelBtnRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
      else if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
     
  }, [opts]);

  const close = (ok: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    if (resolver) resolver(ok);
  };

  return (
    <>
      {children}
      {opts &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) close(false);
            }}
          >
            <div className="max-w-sm w-[90%] rounded-lg bg-white shadow-xl p-5 text-start">
              <h2 id="confirm-title" className="text-lg font-semibold text-slate-900 mb-2">
                {opts.title}
              </h2>
              <p className="text-sm text-slate-700 leading-relaxed mb-4 whitespace-pre-line">
                {opts.message}
              </p>
              <div className="flex gap-2 justify-end flex-row-reverse">
                <button
                  ref={cancelBtnRef}
                  type="button"
                  onClick={() => close(false)}
                  className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium transition"
                >
                  {opts.cancelLabel ?? t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => close(true)}
                  className={
                    opts.destructive
                      ? "px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition"
                      : "px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
                  }
                >
                  {opts.confirmLabel ?? t("common.confirm")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
